import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ScanLine, UserPlus, CalendarCheck, RefreshCw, Activity } from "lucide-react";

/**
 * Recent Activity Feed - last 8 things that happened across the gym.
 * Derived client-side from existing arrays (leads + members) using
 * timestamps that already come back from the dashboard endpoint.
 * Zero new backend calls.
 */

function timeAgo(d) {
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function RecentActivityFeed({ leads = [], members = [], checkins = [], loading }) {
  const events = useMemo(() => {
    const all = [];

    // New leads in the last 7 days
    for (const l of leads.slice(0, 30)) {
      if (!l.created_at) continue;
      all.push({
        id: `lead-${l.id}`,
        when: l.created_at,
        Icon: UserPlus,
        tone: "leads",
        text: <><strong className="text-foreground">{l.name}</strong> came in as a lead</>,
        href: `/leads/${l.id}`,
      });
    }

    // New members (start_date)
    for (const m of members.slice(0, 30)) {
      if (m.start_date) {
        all.push({
          id: `member-start-${m.id}`,
          when: m.start_date,
          Icon: CalendarCheck,
          tone: "growth",
          text: <><strong className="text-foreground">{m.name}</strong> joined the gym</>,
          href: `/members/${m.id}`,
        });
      }
      if (m.last_visit) {
        all.push({
          id: `member-visit-${m.id}`,
          when: m.last_visit,
          Icon: ScanLine,
          tone: "ops",
          text: <><strong className="text-foreground">{m.name}</strong> checked in</>,
          href: `/members/${m.id}`,
        });
      }
    }

    // Backend-provided check-ins (preferred when available)
    for (const c of checkins.slice(0, 20)) {
      if (!c.when || !c.member_name) continue;
      all.push({
        id: `chk-${c.member_id || c.member_name}-${c.when}`,
        when: c.when,
        Icon: ScanLine,
        tone: "ops",
        text: <><strong className="text-foreground">{c.member_name}</strong> checked in</>,
        href: c.member_id ? `/members/${c.member_id}` : "/checkin",
      });
    }

    all.sort((a, b) => new Date(b.when) - new Date(a.when));
    // De-dupe by id, take latest 8
    const seen = new Set();
    return all.filter(e => { if (seen.has(e.id)) return false; seen.add(e.id); return true; }).slice(0, 8);
  }, [leads, members, checkins]);

  const TONE = {
    growth: "text-growth bg-growth/10",
    leads:  "text-leads  bg-leads/10",
    trials: "text-trials bg-trials/10",
    risk:   "text-risk   bg-risk/10",
    ops:    "text-muted-foreground bg-muted",
  };

  return (
    <section data-testid="recent-activity">
      <div className="flex items-end justify-between mb-3">
        <h3 className="font-display text-lg font-bold text-foreground tracking-tight">Recent activity</h3>
      </div>
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading activity...</div>
        ) : events.length === 0 ? (
          <div className="text-center py-10 px-6" data-testid="recent-activity-empty">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-muted text-muted-foreground mb-3">
              <Activity className="w-5 h-5" />
            </div>
            <p className="font-display text-base font-semibold text-foreground">Nothing happening yet.</p>
            <p className="text-sm text-muted-foreground mt-1">Activity will appear here as leads come in and members check in.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {events.map(e => (
              <li key={e.id} className="px-4 py-3 hover:bg-foreground/[0.02] transition-colors">
                <Link to={e.href} className="flex items-center gap-3" data-testid={`activity-${e.id}`}>
                  <span className={`w-8 h-8 rounded-md flex items-center justify-center ${TONE[e.tone] || TONE.ops}`}>
                    <e.Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                  </span>
                  <p className="flex-1 text-sm text-foreground/85 truncate">{e.text}</p>
                  <span className="text-xs text-muted-foreground shrink-0">{timeAgo(e.when)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
